class ProductRecommendations extends HTMLElement {
  #intersectionObserver = new IntersectionObserver(
    (entries, observer) => {
      if (!entries[0]?.isIntersecting) return;
      observer.disconnect();
      this.#loadRecommendations();
    },
    { rootMargin: '0px 0px 400px 0px' }
  );

  #mutationObserver = new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      if (mutation.target !== this || mutation.type !== 'attributes') continue;
      if (mutation.attributeName === 'data-error') continue;
      if (mutation.attributeName === 'class' && this.classList.contains('hidden')) continue;
      if (
        mutation.attributeName === 'data-recommendations-performed' &&
        this.dataset.recommendationsPerformed === 'true'
      )
        continue;
      this.#loadRecommendations();
      break;
    }
  });

  #cachedRecommendations = {};
  #activeFetch = null;

  connectedCallback() {
    this.#intersectionObserver.observe(this);
    this.#mutationObserver.observe(this, { attributes: true });
  }

  #loadRecommendations() {
    const { productId, recommendationsPerformed, sectionId, intent } = this.dataset;
    const id = this.id;

    if (!productId || !id) {
      throw new Error('Product ID and an ID attribute are required');
    }

    if (recommendationsPerformed === 'true') {
      return;
    }

    this.#fetchCachedRecommendations(productId, sectionId, intent)
      .then((result) => {
        if (!result.success) {
          if (!Shopify.designMode) {
            this.#handleError(new Error(`Server returned ${result.status}`));
          }
          return;
        }

        const html = document.createElement('div');
        html.innerHTML = result.data || '';
        const recommendations = html.querySelector(`product-recommendations[id="${id}"]`);

        if (recommendations?.innerHTML && recommendations.innerHTML.trim().length) {
          this.dataset.recommendationsPerformed = 'true';
          this.innerHTML = recommendations.innerHTML;
          
          setTimeout(() => {
            console.log('🔄 Initializing recommended product cards...');
            
            const cards = this.querySelectorAll('product-card');
            console.log('📦 Cards found:', cards.length);
            
            cards.forEach((card, index) => {
              const fieldset = card.querySelector('[data-pc-sizes]');
              const btn = card.querySelector('[data-pc-add]');
              const form = card.querySelector('form[action*="/cart/add"]');
              
              console.log(`Card ${index + 1}:`, {
                fieldset: !!fieldset,
                btn: !!btn,
                form: !!form
              });
              
              if (!fieldset || !btn || !form) return;
              
              // Initially disable button
              btn.disabled = true;
              
              // Listen for radio changes
              fieldset.addEventListener('change', (e) => {
                if (e.target.type === 'radio' && e.target.checked) {
                  console.log(`✅ Radio selected on card ${index + 1}`);
                  
                  const variantId = e.target.dataset.variantId;
                  console.log('Variant ID:', variantId);
                  
                  // Update hidden input
                  const idInput = form.querySelector('input[name="id"]');
                  if (idInput && variantId) {
                    idInput.value = variantId;
                  }
                  
                  btn.disabled = false;
                }
              });
              
              // Add click handler for the button
              btn.addEventListener('click', (e) => {
                e.preventDefault();
                e.stopPropagation();
                
                console.log(`🛒 Add to cart clicked on card ${index + 1}`);
                
                if (btn.disabled) {
                  console.log('Button is disabled, skipping');
                  return;
                }
                
                const selected = fieldset.querySelector('input[type="radio"]:checked');
                if (!selected) {
                  console.log('No variant selected');
                  return;
                }
                
                const variantId = selected.dataset.variantId;
                console.log('Adding variant:', variantId);
                
                // Update form
                const idInput = form.querySelector('input[name="id"]');
                if (idInput) {
                  idInput.value = variantId;
                }
                
                // Try to find and click the real theme button
                const themeBtn = form.querySelector('button[type="submit"]:not([data-pc-add]), button[name="add"]:not([data-pc-add])');
                if (themeBtn) {
                  console.log('Clicking theme button');
                  themeBtn.click();
                } else {
                  console.log('Submitting form directly');
                  form.requestSubmit();
                }
              });
              
              console.log(`✓ Card ${index + 1} initialized`);
            });
          }, 300);
        } else {
          this.#handleError(new Error('No recommendations available'));
        }
      })
      .catch((e) => {
        this.#handleError(e);
      });
  }

  async #fetchCachedRecommendations(productId, sectionId, intent) {
    const url = `${this.dataset.url}&product_id=${productId}&section_id=${sectionId}&intent=${intent}`;

    const cachedResponse = this.#cachedRecommendations[url];
    if (cachedResponse) {
      return { success: true, data: cachedResponse };
    }

    this.#activeFetch?.abort();
    this.#activeFetch = new AbortController();

    try {
      const response = await fetch(url, { signal: this.#activeFetch.signal });
      if (!response.ok) {
        return { success: false, status: response.status };
      }

      const text = await response.text();
      this.#cachedRecommendations[url] = text;
      return { success: true, data: text };
    } finally {
      this.#activeFetch = null;
    }
  }

  #handleError(error) {
    console.error('Product recommendations error:', error.message);
    this.classList.add('hidden');
    this.dataset.error = 'Error loading product recommendations';
  }
}

if (!customElements.get('product-recommendations')) {
  customElements.define('product-recommendations', ProductRecommendations);
}